import { getCookie, removeCookie, setCookie } from './util/cookies';
import { addQueryParamsToUrl } from './util/url';

const guidanceModeKey = 'guidance-mode';
const guidanceStateKey = 'guidance-state';
const guidanceSendMessageKey = 'guidance-send-message';
const guidanceCategoryKey = 'guidance-category';
const guidanceClickModeClass = 'guidance-click-mode';

const showClickMode = () => {
    document.body.classList.add(guidanceClickModeClass);

    setCookie(guidanceModeKey, 'click');
};

const resetGuidanceMode = () => {
    document.body.classList.remove(guidanceClickModeClass);

    setTimeout(() => {
        const chatList = document.getElementById('vngage-chat-list');
        if (chatList) {
            chatList.scrollTo(0, chatList.scrollHeight);
        }
    }, 700);

    removeCookie(guidanceModeKey);
    removeCookie(guidanceStateKey);
};

const hideClickMode = () => {
    resetGuidanceMode();
    setCookie(guidanceModeKey, 'chat');
    setCookie(guidanceCategoryKey, 'XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX, Guidance_Component');
    window.dispatchEvent(new Event('guidance.chat.started'));
};

const isGuidanceActive = () => !!getCookie(guidanceModeKey);
const isGuidanceClickMode = () => getCookie(guidanceModeKey) === 'click';
const getGuidanceChatCategory = () => getCookie(guidanceCategoryKey);

const listenToEscalation = () => {
    window.vngage.subscribe('stream.addConversationEvents', (e, t) => {
        if (!isGuidanceClickMode() && !document.body.classList.contains(guidanceClickModeClass))
            return;
        const data = t && t.events && t.events.length && t.events[0].data;

        if (
            data &&
            data.metadataBag &&
            data.metadataBag.action === 'case.checkGroupStatusAndTransfer' &&
            data.metadataBag.type === 'actionExecuted'
        ) {
            hideClickMode();
        }
    });
};

const listenToSendMessage = () => {
    document.addEventListener('click', (e) => {
        if (
            isGuidanceClickMode() &&
            e.target.id === 'send' &&
            document.querySelector('new-conversation')
        ) {
            window.vngage.leave();
        }
    });
};

let guidanceState = { message: '', options: [], click: undefined, parentIds: {}, questions: {} };

const saveGuidanceState = () => {
    if (!isGuidanceClickMode()) return;
    setCookie(guidanceStateKey, JSON.stringify(guidanceState));
};

const restoreGuidanceState = () => {
    if (!isGuidanceClickMode()) return;
    const savedState = getCookie(guidanceStateKey);
    if (!savedState) return;
    try {
        guidanceState = JSON.parse(savedState);
    } catch {
        // nothing
    }
};

const isMyPages = () => {
    const hostname = window.location.hostname;
    const mypages = ['omatsivut', 'minasidor', 'minesider', 'mypages'];
    return mypages.find((str) => hostname.includes(str));
};

const createDataLayerEvent = (props) => ({
    event: 'User Guidance',
    processInfo: {
        details: {
            answer: props.answer || '',
            button_type: props.button_type || '',
            question: props.question || '',
        },
        name: isMyPages() ? 'mypages' : 'openpages',
        part: props.part || '',
        processStep: {
            mainStep: props.mainStep || '',
        },
        subname: 'guidancecomponent',
    },
});

const pushStartEventToDataLayer = () => {
    if (typeof window.dataLayer !== 'object') return;
    const event = createDataLayerEvent({
        part: '0_',
        answer: 'Guidance component launched',
    });
    window.dataLayer.push(event);
};

const trackGuidanceState = () => {
    const getQuestionNumber = (id) => (id ? getQuestionNumber(guidanceState.parentIds[id]) + 1 : 0);

    const pushEventToDataLayer = () => {
        if (typeof window.dataLayer !== 'object') return;
        if (!guidanceState.click) return;
        const questionNumber = getQuestionNumber(guidanceState.click.id);
        const part = questionNumber + '_' + (guidanceState.parentIds[guidanceState.click.id] || '');
        const question =
            guidanceState.questions[guidanceState.click.id] || guidanceState.message || '';
        const event = createDataLayerEvent({
            part: part,
            question: question,
            answer: guidanceState.click.text,
            mainStep: guidanceState.click.id,
            button_type: 'action_link',
        });
        window.dataLayer.push(event);
    };

    const updateGuidanceStateParentIds = () => {
        if (!guidanceState.click) return;
        const parentId = guidanceState.click.id;
        for (const option of guidanceState.options) {
            guidanceState.parentIds[option.id] = parentId;
        }
    };

    const updateGuidanceStateQuestions = () => {
        if (!guidanceState.message) return;
        for (const option of guidanceState.options) {
            guidanceState.questions[option.id] = guidanceState.message;
        }
    };

    const updateGuidanceState = (add) => {
        const data = add && add.events && add.events.length && add.events[0].data;

        if (data && data.messageType === 'chat' && data.message) {
            guidanceState.message = data.message;
        }
        if (data && data.metadataBag && data.metadataBag.options) {
            guidanceState.options = data.metadataBag.options.map((o) => ({
                text: o.text,
                id: o.accepts && o.accepts.length ? o.accepts[0] : '',
            }));
            updateGuidanceStateParentIds();
            updateGuidanceStateQuestions();
        }
        if (
            data &&
            data.metadataBag &&
            data.metadataBag.linkName &&
            (data.metadataBag.actionId || data.metadataBag.externalId)
        ) {
            guidanceState.click = {
                text: data.metadataBag.linkName,
                id: data.metadataBag.actionId || data.metadataBag.externalId,
            };
            pushEventToDataLayer();
        }
    };

    window.vngage.subscribe('stream.addConversationEvents', (e, t) => {
        updateGuidanceState(t);
        saveGuidanceState();
    });
};

const showGuidanceComponent = (sendMessageUrl) => {
    window.ifhelp2015api
        .featureTogglePromise('ShowGuidanceComponent')
        .then(() => showGuidanceComponentWithToggle(sendMessageUrl, true))
        .catch(() => showGuidanceComponentWithToggle(sendMessageUrl, false));
};

const isChatActive = () => window.vngage?.isChatActive?.() || false;
const isChatCompleted = () => window.vngage?.isChatCompleted?.() || false;

const showGuidanceComponentWithToggle = (sendMessageUrl, showGuidanceComponentToggle) => {
    const restorePanel = () => {
        const panelStateKey = 'vngage.panel:state';
        const panelState = window.localStorage.getItem(panelStateKey);
        if (!panelState) return;
        try {
            const json = JSON.parse(panelState);
            if (!json.state?.startsWith('idle-')) return;
            json.state = 'docked-right';
            window.localStorage.setItem(panelStateKey, JSON.stringify(json));
            location.reload();
        } catch {
            // Nothing
        }
    };
    if (
        showGuidanceComponentToggle &&
        ((isChatActive() && isGuidanceClickMode()) || isChatCompleted())
    ) {
        restorePanel();
        return;
    }
    if (
        !showGuidanceComponentToggle ||
        isChatActive() ||
        !window.vngage ||
        !window.vngage.canJoinGuidance ||
        !window.vngage.canJoinGuidance() ||
        !window.vngage.joinGuidance
    ) {
        if (sendMessageUrl) window.location.href = sendMessageUrl;
        return;
    }

    if (sendMessageUrl) setCookie(guidanceSendMessageKey, sendMessageUrl);

    applyGuidanceTheme();
    showClickMode();
    listenToEscalation();
    listenToSendMessage();
    trackGuidanceState();
    pushStartEventToDataLayer();

    window.vngage.setChatAppType?.('Guidance Clickbot');
    window.vngage.joinGuidance();

    setTimeout(() => {
        if (window.vngage && window.vngage.get('currentstate') === 'Queued') {
            window.vngage.leave();
            if (sendMessageUrl) window.location.href = sendMessageUrl;
        }
    }, 7000);
};

const applyGuidanceTheme = () => {
    const themeIds = window.vngage.themeIds;
    if (!themeIds || !themeIds.guidance) return;

    window.vngage.set('appConfig', 'themes.dialog', themeIds.guidance);

    const styles = document.querySelector('link#vngageThemeCSS');
    if (!styles || !styles.href) return;

    styles.href =
        styles.href.substring(0, styles.href.lastIndexOf('/') + 1) + themeIds.guidance + '.css';
};

const redirectToSendMessage = (category) => {
    const minimizePanel = () => {
        const panelStateKey = 'vngage.panel:state';
        const panelState = window.localStorage.getItem(panelStateKey);
        if (!panelState) return;
        try {
            const json = JSON.parse(panelState);
            json.state = 'idle-right';
            window.localStorage.setItem(panelStateKey, JSON.stringify(json));
        } catch {
            // Nothing
        }
    };

    const sendMessageUrl = getCookie(guidanceSendMessageKey);
    if (sendMessageUrl) {
        const sendMessageUrlWithParams = addQueryParamsToUrl(sendMessageUrl, [
            { name: 'category', value: category },
            { name: 'itm_source', value: 'clickbot' },
            { name: 'itm_medium', value: 'link' },
            { name: 'itm_campaign', value: 'securemessage' }
        ]);

        minimizePanel();
        window.location.href = sendMessageUrlWithParams;
    }
};

const decorateWithLanguageAttribute = () => {
    const lang = document.documentElement?.lang;

    if (!lang) return;

    document.querySelector('.vngage-visitor-app').setAttribute('data-lang', lang);
};

let guidanceObserver = null;

const observeGuidanceChanges = () => {
    if (guidanceObserver) return;
    const chatList = document.getElementById('vngage-chat-list');
    if (!chatList) return;

    const updateLink = (link) => {
        const url = link.href || '';
        const index = url.lastIndexOf(')');
        link.href = index ? url.substring(0, index + 1) + ';' : url;
        link.setAttribute('target', '_self');
    };

    const updateStartChatLink = (element) => {
        const prefix = `javascript:vngage.hideClickMode`;
        const links = element.querySelectorAll(`a.vngage-btn-link[href^='${prefix}']`);
        [...links].forEach((link) => updateLink(link));
    };

    const updateSendMessageLink = (element) => {
        const prefix = `javascript:vngage.redirectToSendMessage`;
        const links = element.querySelectorAll(`a.vngage-btn-link[href^='${prefix}']`);
        [...links].forEach((link) => updateLink(link));
    };

    const config = { childList: true };
    const callback = (mutationList /*, observer*/) => {
        for (const mutation of mutationList) {
            for (const node of mutation.addedNodes) {
                updateStartChatLink(node);
                updateSendMessageLink(node);
            }
        }
    };

    const observer = new MutationObserver(callback);
    observer.observe(chatList, config);
    guidanceObserver = observer;
};

const disposeGuidanceComponent = () => {
    if (guidanceObserver) {
        guidanceObserver.disconnect();
        guidanceObserver = null;
    }

    resetGuidanceMode();
    removeCookie(guidanceModeKey);
    removeCookie(guidanceStateKey);
    removeCookie(guidanceSendMessageKey);
    removeCookie(guidanceCategoryKey);
};

const addGuidanceComponent = () => {
    window.vngage.isGuidanceActive = isGuidanceActive;
    window.vngage.showClickMode = showClickMode;
    window.vngage.hideClickMode = hideClickMode;
    window.vngage.showGuidanceComponent = showGuidanceComponent;
    window.vngage.redirectToSendMessage = redirectToSendMessage;

    window.vngage.subscribe('chat.initialized', () => {
        if (isGuidanceActive()) {
            observeGuidanceChanges();
            decorateWithLanguageAttribute();
        }
    });

    window.vngage.subscribe('message_rendered', () => {
        if (isGuidanceActive()) observeGuidanceChanges();
    });

    window.vngage.subscribe('app.initialized', () => {
        if (!isChatActive()) return; // Returns chat active status after app.initialized
        if (isGuidanceActive()) applyGuidanceTheme();
        if (isGuidanceClickMode()) {
            showClickMode();
            listenToEscalation();
            listenToSendMessage();
            trackGuidanceState();
            restoreGuidanceState();
        }
    });
};

export {
    addGuidanceComponent,
    disposeGuidanceComponent,
    getGuidanceChatCategory,
    isGuidanceClickMode,
};
